import { Store } from '@ngrx/store';
import {
	selectAccountSlug,
	selectAuth,
	selectClientLocation,
	selectConfiguracionCanal,
	selectDeliveryZoneList,
	selectLanguaje,
	selectLocalChannelSesion,
	selectSlug,
	selectStore,
	selectStoreLocation,
} from '@store/selectors';
import { Injectable, signal, WritableSignal } from '@angular/core';
import { AuthModalService, CartService, I18nService, StoreService } from '@core/services';
import { ComponentEnum, LanguajeEnum } from '@core/enums';
import { IAbstractPresenter, ILanguageObject } from '@core/interfaces';
import { IAppComponent, IAppPresenter, IPage } from './interfaces';
import { WebsocketService } from '@core/services/websocket.service';
import { combineLatest, combineLatestWith, filter, map, Observable, takeUntil, tap, withLatestFrom } from 'rxjs';
import { Authentication, MenuItem, MenuItemUpsert } from '@core/models';
import {
	AUTH_LOGOUT,
	AUTH_REFRESH_TOKEN,
	CART_UPDATE_STOCK,
	CLIENT_FIND_LOCATION,
	CORE_CHANGE_LANGUAJE,
	DELIVERY_ZONE_FIND_BY_LOCATION,
} from '@store/actions';
import { ChannelProcessorService } from './core/domain';
import appEn from '@/i18n/app.en.json';
import { MenuController } from '@ionic/angular';
import { AuthProcessorService } from './core/domain/auth/auth-processor.service';
import { CartProcessorService } from './features/shoppingcart/domain';

@Injectable({ providedIn: 'root' })
export class AppPresenter extends IAbstractPresenter<IAppComponent> implements IAppPresenter {
	isMenuDisabled = true;
	appPages: IPage[] = [];
	userName: string | null = null;
	userEmail: string | null = null;
	auth$: Observable<Authentication | null>;
	slugSignal: WritableSignal<string | null> = signal(null);
	isLoggedInSignal: WritableSignal<boolean> = signal(false);
	isCartEmptySignal: WritableSignal<boolean> = signal(false);
	cart$: Observable<MenuItem[]>;
	translations: ILanguageObject<typeof appEn>;
	language$: Observable<LanguajeEnum>;
	baseUrlSignal: WritableSignal<string | null> = signal(null);

	constructor(
		private cartService: CartService,
		private store: Store,
		private i18nService: I18nService<typeof appEn>,
		private menuCtrl: MenuController,
		private authModalService: AuthModalService,
		private websocketService: WebsocketService,
		private channelProcessor: ChannelProcessorService,
		private authProcessorService: AuthProcessorService,
		private cartProcessorService: CartProcessorService,
		private storeService: StoreService
	) {
		super();

		this.translations = this.i18nService.getTranslations(ComponentEnum.App);
		this.auth$ = this.store.select(selectAuth);
		this.language$ = this.store.select(selectLanguaje);
		this.translations = this.i18nService.getTranslations(ComponentEnum.App);
		this.cart$ = this.cartService.getStoreForStore();

		this.appPages = [
			{
				title: 'Home',
				url: '',
				icon: 'home',
			},
			{
				title: 'My Profile',
				url: `profile`,
				icon: 'user',
			},
		];
	}

	dispatchActions(): void {
		this.store.dispatch(CLIENT_FIND_LOCATION());
		this.store.dispatch(AUTH_REFRESH_TOKEN());
	}

	suscribeObservers(): void {
		this.getNavigationLanguage();

		this.language$
			.pipe(takeUntil(this.destroy$))
			.subscribe(lang => (this.view.translations = this.i18nService.getTranslations(ComponentEnum.App, lang)));

		this.store
			.select(selectAccountSlug)
			.pipe(withLatestFrom(this.store.select(selectSlug)), takeUntil(this.destroy$))
			.subscribe(([accountSlug, slug]) => {
				this.baseUrlSignal.set(accountSlug + '/' + slug);
			});

		this.store
			.select(selectClientLocation)
			.pipe(
				combineLatestWith(this.store.select(selectStoreLocation), this.store.select(selectDeliveryZoneList)),
				filter(([location1, location2, location3]) => !!location1 || !!location2 || !!location3),
				takeUntil(this.destroy$)
			)
			.subscribe(() => this.store.dispatch(DELIVERY_ZONE_FIND_BY_LOCATION()));

		combineLatest([this.store.select(selectStore), this.store.select(selectLocalChannelSesion)])
			.pipe(
				map(([store, data]) => this.channelProcessor.process(data, store)),
				filter(payload => !!payload),
				tap(({ localCanalId, token }) => this.websocketService.connect(token, localCanalId)),
				takeUntil(this.destroy$)
			)
			.subscribe();

		this.websocketService
			.getStockUpdates()
			.pipe(
				filter((updateProduct: MenuItemUpsert | null) => !!updateProduct),
				map((updateProduct: MenuItemUpsert) => updateProduct),
				takeUntil(this.destroy$)
			)
			.subscribe(updateProduct => this.store.dispatch(CART_UPDATE_STOCK({ updateProduct })));

		this.cart$
			.pipe(
				map(cart => this.cartProcessorService.isCartEmpty(cart)),
				takeUntil(this.destroy$)
			)
			.subscribe((isCartEmpty: boolean) => this.isCartEmptySignal.set(isCartEmpty));

		this.auth$
			.pipe(
				map(auth => this.authProcessorService.processAuthCalculation(auth)),
				takeUntil(this.destroy$)
			)
			.subscribe(({ isLoggedIn, userName, userEmail }) => {
				this.view.userName = userName;
				this.view.userEmail = userEmail;
				this.isLoggedInSignal.set(!!isLoggedIn);

				this.cdr.detectChanges();
			});
	}

	getNavigationLanguage(): void {
		// Obtener el idioma del navegador
		const browserLang = navigator.language || navigator.languages?.[0] || LanguajeEnum.en;

		// Extraer el código de idioma principal (ej: 'es' de 'es-ES', 'en' de 'en-US')
		const primaryLang = browserLang.split('-')[0].toLowerCase();

		// Verificar si ya hay un idioma guardado en localStorage
		const savedLang = localStorage.getItem('lang');

		// Si no hay idioma guardado, usar el del navegador
		if (!savedLang) {
			// Mapear idiomas soportados
			let appLang: LanguajeEnum;

			if (primaryLang === LanguajeEnum.es) {
				appLang = LanguajeEnum.es;
			} else {
				// Por defecto usar inglés para cualquier otro idioma
				appLang = LanguajeEnum.en;
			}
			// Guardar el idioma detectado
			localStorage.setItem('lang', appLang);

			// Dispatch la acción para cambiar el idioma en el store
			this.store.dispatch(CORE_CHANGE_LANGUAJE({ languaje: appLang }));
		} else {
			// Si ya hay un idioma guardado, usarlo
			const savedLangTyped = savedLang as LanguajeEnum;
			this.i18nService.setLanguage(savedLangTyped);
			this.store.dispatch(CORE_CHANGE_LANGUAJE({ languaje: savedLangTyped }));
		}
	}

	closeSesion(): void {
		this.store.dispatch(AUTH_LOGOUT());
		this.hideMenuSidebar();
	}

	async openSignInModal(): Promise<void> {
		await this.authModalService.openSignInModal({});
	}

	attrLabelSignIn(): string {
		return this.translations.signIn ?? '';
	}

	async hideMenuSidebar(): Promise<void> {
		this.menuCtrl.close();
	}

	navigateToUrl(page: IPage): string {
		return `${this.baseUrlSignal()}/${page.url}`;
	}
}
